/**
 * Carousel - general-purpose carousel built on Swiper; breakpoint-gated init, auto-built navigation/pagination/scrollbar, thumbnail pairing.
 * Custom element: <theme-carousel>
 *
 * Attributes (from $info):
 *   navigation                      Boolean  false
 *   svg                             String   ""
 *   pagination                      Boolean  false
 *   use-hash-navigation             Boolean  false
 *   mousewheel                      Boolean  false
 *   clickable-pagination            Boolean  false
 *   scrollbar                       Boolean  false
 *   breakpoint                      Number   null
 *   pagination-count                Boolean  false
 *   is-thumbnail-slider             Boolean  false
 *   thumbnail-slider-class          String   null
 *   ignore-one-slide-in-view-class  Boolean  false
 *   props-wrapper                   String   ""
 *   option-index                    Number   -1
 *   slider-id                       String   -
 *   desktop-slides                  Number   null
 *   tablet-slides                   Number   null
 *   mobile-slides                   Number   null
 *   desktop-gap                     Number   -
 *   tablet-gap                      Number   -
 *   mobile-gap                      Number   -
 *   partial-slide                   Boolean  -
 *   center-insufficient-slides      Number   -
 *   autoplay                        Boolean  false
 *   loop                            Boolean  false
 *   direction                       String   "horizontal"
 *   settings                        Object   -
 *
 * Refs:
 *   [data-ref="swiperWrapper"]  (required)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) =>
  defineProperty(target, "name", {
    value: functionName,
    configurable: !0,
  });
import "./component-ProductOptionPicker.js";
import {
  dist as vendorLib,
  Swiper,
  Navigation,
  Pagination,
  Scrollbar,
  Autoplay,
  Thumb,
  Controller,
  Mousewheel,
  EffectFade,
  Zoom,
  HashNavigation,
} from "./vendor.js";
import { ThemeComponent } from "./theme.js";
import "./component-ProductButtonOption.js";
import "./component-ProductDropdownOption.js";
import "./component-FakeDropdown.js";
class Carousel extends ThemeComponent {
  constructor() {
    (super(...arguments),
      (this._onSwiperInit = new vendorLib.SimpleEventDispatcher()),
      (this.swiper = void 0));
  }
  get onSwiperInit() {
    return this._onSwiperInit.asEvent();
  }
  init() {
    if (this.breakpoint !== null) {
      const mediaQuery = window.matchMedia(
          `(min-width: ${this.breakpoint + 1}px)`,
        ),
        breakpointChecker = setFunctionName(() => {
          if (mediaQuery.matches) {
            this.swiper !== void 0 && this.swiper.destroy(!0, !0);
            return;
          } else this.setupSwiper();
        }, "breakpointChecker");
      (breakpointChecker(),
        mediaQuery.addEventListener("change", () => {
          breakpointChecker();
        }));
    } else this.setupSwiper();
  }
  setupSwiper() {
    var parentRef;
    (this.setDefaultNavIcon(),
      this.constructSettings(),
      this.addSliderClasses(),
      (this.navigation || this.pagination || this.scrollbar) &&
        this.checkPropsAddElements(),
      this.useHashNavigation &&
        (this.settings = {
          ...this.settings,
          hashNavigation: {
            watchState: !0,
          },
        }));
    const thumbnailSlider = this.thumbnailSliderClass
      ? (parentRef = this.parentElement) == null
        ? void 0
        : parentRef.querySelector(`${this.thumbnailSliderClass}`)
      : null;
    if (
      (thumbnailSlider != null && thumbnailSlider.swiper
        ? ((this.settings.thumbs = {
            swiper: thumbnailSlider.swiper,
          }),
          (this.swiper = this.initialiseSwiper()))
        : thumbnailSlider
          ? this.$subscribe(thumbnailSlider.onSwiperInit, (thumbsSwiper) => {
              ((this.settings.thumbs = {
                swiper: thumbsSwiper,
              }),
                (this.swiper = this.initialiseSwiper()));
            })
          : (this.swiper = this.initialiseSwiper()),
      (this.pagination || this.settings.pagination) &&
        this.paginationCount &&
        this.swiper)
    ) {
      const paginationElement = this.querySelector(".swiper-pagination"),
        countHtml = `
                <span class="swiper-pagination__count">
                    <span class="swiper-pagination__index">
                        ${this.swiper.activeIndex + 1}
                    </span>
                        /${this.swiper.slides.length}
                </span>`;
      (paginationElement == null ||
        paginationElement.insertAdjacentHTML("afterbegin", countHtml),
        this.swiper.on("slideChange", () => {
          var swiperRef;
          const indexElement = this.querySelector(".swiper-pagination__index");
          indexElement &&
            this.swiper &&
            (indexElement.innerText = String(
              ((swiperRef = this.swiper) == null
                ? void 0
                : swiperRef.activeIndex) + 1,
            ));
        }));
    }
    this.swiper &&
      (this.setActiveClass(Number(this.getActiveindex())),
      this.swiper.on("slideChange", (changeEvent) => {
        this.setActiveClass(changeEvent.activeIndex);
      }),
      this.updateEdgeClasses(),
      this.swiper.on("progress", () => this.updateEdgeClasses()),
      this.swiper.on("slideChange", () => this.updateEdgeClasses()),
      this.swiper.on("resize", () => this.updateEdgeClasses()));
  }
  updateEdgeClasses() {
    this.swiper &&
      (this.classList.toggle("swiper--at-start", this.swiper.isBeginning),
      this.classList.toggle("swiper--at-end", this.swiper.isEnd));
  }
  setActiveClass(activeIndex) {
    const slideNumber = activeIndex + 1,
      carousel = this.closest(".exhibition-carousel"),
      textSlides =
        carousel == null
          ? void 0
          : carousel.querySelectorAll(".text-for-slides li");
    if (textSlides)
      for (const textSlide of textSlides)
        textSlide == null ||
          textSlide.classList.remove("text-for-slides__active");
    const activeTextSlide =
      carousel == null
        ? void 0
        : carousel.querySelector(`.text-for-slides__slide${slideNumber}`);
    activeTextSlide == null ||
      activeTextSlide.classList.add("text-for-slides__active");
  }
  constructSettings() {
    ((this.settings.direction =
      this.direction === "vertical" ? "vertical" : "horizontal"),
      (this.settings.slidesPerView = this.mobileSlides || "auto"),
      (this.settings.spaceBetween = this.mobileGap),
      this.partialSlide &&
        this.desktopSlides &&
        (this.desktopSlides = this.desktopSlides + 0.5),
      this.partialSlide &&
        this.tabletSlides &&
        (this.tabletSlides = this.tabletSlides + 0.5),
      (this.settings.centerInsufficientSlides =
        !!this.centerInsufficientSlides),
      (this.settings.loop = this.loop),
      this.autoplay &&
        ((this.settings.speed = 6e3),
        (this.settings.autoplay = {
          delay: 0,
          disableOnInteraction: !0,
        })),
      (this.settings.breakpoints = {
        768: {
          slidesPerView: this.tabletSlides || "auto",
          spaceBetween: this.tabletGap,
        },
        1024: {
          slidesPerView: this.desktopSlides || "auto",
          spaceBetween: this.desktopGap,
        },
      }));
    const navRightElement = document.querySelector(
        `#${this.sliderId}__nav-right`,
      ),
      navLeftElement = document.querySelector(`#${this.sliderId}__nav-left`);
    navRightElement &&
      navLeftElement &&
      (this.settings.navigation = {
        nextEl: `#${this.sliderId}__nav-right`,
        prevEl: `#${this.sliderId}__nav-left`,
      });
  }
  setDefaultNavIcon() {
    !this.svg &&
      this.navigation &&
      (this.svg = `
            <svg class="icon" viewBox="0 0 24 24" fill="none" role="presentation" xmlns="http://www.w3.org/2000/svg">
                <path 
                    d="M19 12H5M5 12L12 19M5 12L12 5" 
                    stroke="currentColor"
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round"
                />
            </svg>
            `);
  }
  addSliderClasses() {
    (this.classList.add("swiper"),
      this.navigation && this.classList.add("swiper--has-navigation"),
      this.pagination && this.classList.add("swiper--has-pagination"),
      this.scrollbar && this.classList.add("swiper--has-scrollbar"),
      this.settings.slidesPerView &&
        this.settings.slidesPerView === 1 &&
        !this.ignoreOneSlideInViewClass &&
        this.classList.add("swiper--one-slide-in-view"));
    const wrapperChildren = this.swiperWrapper.children;
    for (const child of wrapperChildren)
      child.classList.contains("swiper-slide") ||
        child.classList.add("swiper-slide");
  }
  checkPropsAddElements() {
    const propsContainer = document.createElement("div");
    propsContainer.className = "swiper-props";
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (
      ((this.settings.mousewheel = this.mousewheel),
      this.settings.mousewheel &&
        (this.settings.mousewheel = {
          forceToAxis: !0,
        }),
      this.navigation && !this.settings.navigation)
    ) {
      const prevButton = document.createElement("button"),
        prevLabel = document.createElement("span");
      (prevButton.setAttribute("role", "button"),
        prevButton.setAttribute("type", "button"),
        prevButton.classList.add("swiper-prev"),
        prevButton.classList.add("swiper-navigation"),
        prevLabel.classList.add("visually-hidden"),
        (prevLabel.innerHTML = "Previous"),
        (prevButton.innerHTML = this.svg),
        prevButton.append(prevLabel),
        slideCount > 1 && propsContainer.appendChild(prevButton));
    }
    if (this.pagination || this.settings.pagination) {
      const paginationContainer = document.createElement("div");
      (paginationContainer.classList.add("swiper-pagination"),
        slideCount > 1 && propsContainer.appendChild(paginationContainer));
      const paginationType =
        typeof this.settings.pagination == "object"
          ? this.settings.pagination.type
          : "bullets";
      this.settings.pagination = {
        el: ".swiper-pagination",
        type: paginationType,
        clickable: this.clickablePagination,
      };
    }
    if (this.navigation && !this.settings.navigation) {
      const nextButton = document.createElement("button"),
        nextLabel = document.createElement("span");
      (nextButton.setAttribute("role", "button"),
        nextButton.setAttribute("type", "button"),
        nextButton.classList.add("swiper-next"),
        nextButton.classList.add("swiper-navigation"),
        nextLabel.classList.add("visually-hidden"),
        (nextLabel.innerHTML = "Next"),
        (nextButton.innerHTML = this.svg),
        nextButton.append(nextLabel),
        slideCount > 1 && propsContainer.appendChild(nextButton),
        (this.settings.navigation = {
          nextEl: ".swiper-next",
          prevEl: ".swiper-prev",
        }));
    }
    if (this.scrollbar || this.settings.scrollbar) {
      const scrollbarContainer = document.createElement("div");
      (scrollbarContainer.classList.add("swiper-scrollbar"),
        this.appendChild(scrollbarContainer),
        (this.settings.scrollbar = {
          el: ".swiper-scrollbar",
          draggable: !0,
          hide: !1,
        }));
    }
    if (this.propsWrapper) {
      const propsWrapperElement = this.querySelector(this.propsWrapper);
      if (propsWrapperElement) {
        propsWrapperElement == null ||
          propsWrapperElement.appendChild(propsContainer);
        return;
      }
    }
    this.appendChild(propsContainer);
  }
  initialiseSwiper() {
    return (
      Swiper.use([
        Navigation,
        Pagination,
        Scrollbar,
        Autoplay,
        Thumb,
        Controller,
        Mousewheel,
        EffectFade,
        Zoom,
        HashNavigation,
      ]),
      (this.swiper = new Swiper(this, this.settings)),
      this._onSwiperInit.dispatch(this.swiper),
      this.swiper
    );
  }
  manualUpdate() {
    var swiperRef, slidesRef, slideToRef;
    ((swiperRef = this.swiper) == null || swiperRef.update(),
      (slidesRef = this.swiper) == null || slidesRef.updateSlides(),
      (slideToRef = this.swiper) == null || slideToRef.slideTo(0, 300, !1));
  }
  getActiveindex() {
    var swiperRef;
    return (swiperRef = this.swiper) == null ? void 0 : swiperRef.activeIndex;
  }
}
setFunctionName(Carousel, "ThemeCarousel");
const propertyTypes = {
  Boolean,
  String,
  Number,
  Object,
};
Carousel.prototype.$name = "ThemeCarousel";
Carousel.prototype.$info = JSON.parse(
  `{"name":"ThemeCarousel","meta":{"isLegacy":false,"selector":"theme-carousel"},"props":[{"attr":"navigation","default":false,"reflectAttr":false,"propertyKey":"navigation","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"svg","default":"","reflectAttr":false,"propertyKey":"svg","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string"},{"attr":"pagination","default":false,"reflectAttr":false,"propertyKey":"pagination","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"use-hash-navigation","default":false,"reflectAttr":false,"propertyKey":"useHashNavigation","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"mousewheel","default":false,"reflectAttr":false,"propertyKey":"mousewheel","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"clickable-pagination","default":false,"reflectAttr":false,"propertyKey":"clickablePagination","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"scrollbar","default":false,"reflectAttr":false,"propertyKey":"scrollbar","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"breakpoint","default":null,"reflectAttr":false,"propertyKey":"breakpoint","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number|null"},{"attr":"pagination-count","default":false,"reflectAttr":false,"propertyKey":"paginationCount","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"is-thumbnail-slider","default":false,"reflectAttr":false,"propertyKey":"isThumbnailSlider","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"thumbnail-slider-class","default":null,"reflectAttr":false,"propertyKey":"thumbnailSliderClass","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string|null"},{"attr":"ignore-one-slide-in-view-class","default":false,"reflectAttr":false,"propertyKey":"ignoreOneSlideInViewClass","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"props-wrapper","default":"","reflectAttr":false,"propertyKey":"propsWrapper","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string"},{"attr":"option-index","default":-1,"reflectAttr":false,"propertyKey":"optionIndex","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number"},{"attr":"slider-id","reflectAttr":false,"propertyKey":"sliderId","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string"},{"attr":"desktop-slides","default":null,"reflectAttr":false,"propertyKey":"desktopSlides","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number|null"},{"attr":"tablet-slides","default":null,"reflectAttr":false,"propertyKey":"tabletSlides","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number|null"},{"attr":"mobile-slides","default":null,"reflectAttr":false,"propertyKey":"mobileSlides","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number|null"},{"attr":"desktop-gap","reflectAttr":false,"propertyKey":"desktopGap","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number"},{"attr":"tablet-gap","reflectAttr":false,"propertyKey":"tabletGap","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number"},{"attr":"mobile-gap","reflectAttr":false,"propertyKey":"mobileGap","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number"},{"attr":"partial-slide","reflectAttr":false,"propertyKey":"partialSlide","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"center-insufficient-slides","reflectAttr":false,"propertyKey":"centerInsufficientSlides","propertyType":"Number","propertyTypeName":"Number","propertyTsTypeName":"number"},{"attr":"autoplay","default":false,"reflectAttr":false,"propertyKey":"autoplay","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"loop","default":false,"reflectAttr":false,"propertyKey":"loop","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"direction","default":"horizontal","reflectAttr":false,"propertyKey":"direction","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"'horizontal'|'vertical'"},{"attr":"settings","reflectAttr":false,"propertyKey":"settings","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"SwiperOptions"}],"refs":[{"selector":"[data-ref=\\"swiperWrapper\\"]","required":true,"propertyKey":"swiperWrapper","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLDivElement"}]}`,
);
Carousel.prototype.$info.component = Carousel;
for (const prop of Carousel.prototype.$info.props)
  prop.propertyType = propertyTypes[prop.propertyType];
window.componentService.registerComponent(
  "ThemeCarousel/ThemeCarousel.ts",
  {
    ThemeCarousel: Carousel,
  },
  void 0,
);
